/** Shared visual constants for all ScadFeature SVG diagrams. */

export const D = {
  // SVG viewport
  W: 280,
  H: 150,

  // Axis colors (OpenSCAD convention)
  X_COLOR: '#ef4444', // red
  Y_COLOR: '#22c55e', // green
  Z_COLOR: '#3b82f6', // blue

  // Geometry strokes
  GEO_STROKE: '#60a5fa',    // blue-400
  GEO_FILL: 'rgba(96,165,250,0.07)',
  GEO_STROKE_GHOST: 'rgba(96,165,250,0.3)',
  GEO_FILL_GHOST: 'rgba(96,165,250,0.04)',

  // Dimension / annotation lines
  DIM_STROKE: '#6b7280',    // gray-500
  DIM_DASH: '4 3',

  // Label text
  LABEL_FILL: 'currentColor',
  LABEL_OPACITY: '0.7',
  FONT: '10px monospace',
  FONT_SM: '9px monospace',

  // Origin cross
  ORIGIN_COLOR: '#9ca3af',  // gray-400
} as const;

/** Arrowhead marker definition — call once per SVG via <defs> */
export const ARROW_MARKER_ID = 'dia-arrow';
export const ARROW_MARKER_REV_ID = 'dia-arrow-rev';

export function arrowheadDef(color = '#6b7280') {
  return `<marker id="${ARROW_MARKER_ID}" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
    <path d="M0,0 L6,3 L0,6 Z" fill="${color}" />
  </marker>`;
}

/** Draw an annotated dimension line with arrowheads at both ends.
 *  Returns SVG element string fragments (for use in template literals). */
export function dimLine(
  x1: number, y1: number,
  x2: number, y2: number,
  label: string,
  labelOffX = 0, labelOffY = -5,
  color = D.DIM_STROKE,
): string {
  const mx = (x1 + x2) / 2 + labelOffX;
  const my = (y1 + y2) / 2 + labelOffY;
  return `
  <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"
        stroke="${color}" stroke-width="1" stroke-dasharray="${D.DIM_DASH}"
        marker-end="url(#dim-arrow-end)" marker-start="url(#dim-arrow-start)" />
  <text x="${mx}" y="${my}" font="${D.FONT}" font-size="10" font-family="monospace"
        fill="${color}" opacity="${D.LABEL_OPACITY}" text-anchor="middle">${label}</text>`;
}

/** Clamp a value to [min, max]. */
export function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

/** Normalize a size value to SVG pixels, clamped to [minPx, maxPx]. */
export function normSize(v: number, ref: number, minPx = 10, maxPx = 80): number {
  return clamp((v / ref) * 60, minPx, maxPx);
}
